/**
 * The words of the Overview, Financial Intelligence, AI Insights and Executive Reports pages, in English and
 * Arabic. Every key here needs its Arabic: `areaAAr` is typed on `areaAEn`.
 */

export const areaAEn = {
  // ---- Executive Overview -------------------------------------------------------------------------
  'overview.title': 'Executive Overview',
  'overview.subtitle': 'The company’s period at a glance — Finance’s own figures, the work, the people, delivery and health.',
  'overview.snapshot': 'Stored report · revision {revision}',
  'overview.noSnapshot': 'These figures are computed now; no report of this period is stored yet.',
  'overview.toReports': 'Make one in Executive Reports',
  'overview.ai.title': 'AI briefing for this period',
  'overview.ai.latestDaily': 'Latest daily briefing — {date}',
  'overview.ai.openReport': 'Open the report',
  'overview.ai.allInsights': 'All AI insights',
  'overview.ai.notThisPeriod': 'This period has no briefing of its own: {reason}',
  'overview.chart.asTable': 'Show the figures as a table',
  'overview.chart.trendLabel': 'Chart: collected, expenses and the result by month, {from} to {to}, in {currency}.',
  'overview.chart.gaps': 'Gaps — not known: {months}',
  'overview.chart.faded': 'Faded — Finance could not fully confirm: {months}',
  'overview.trend.title': 'Finance by month',
  'overview.trend.subtitle': 'Twelve months to the period’s last, in {currency}.',
  'overview.trend.open': 'Open the trends',
  'overview.work.title': 'Work this period',
  'overview.work.subtitle': 'Against {basis} — each work item counted once.',
  'overview.work.open': 'Work & Productivity',
  'overview.work.now': 'This period',
  'overview.work.before': 'The period before',
  'overview.work.completed': 'Items completed',
  'overview.work.completedHint': 'Items completed are root items: tasks outside a workflow, and workflows completed — each once.',
  'overview.work.created': 'Tasks created',
  'overview.work.ticketsCreated': 'Tickets created',
  'overview.work.steps': 'Workflow steps completed',
  'overview.work.resolved': 'Tickets resolved',
  'overview.work.overdueNow': 'Tasks overdue now',
  'overview.work.dueSoon': 'Due within two days',
  'overview.work.viewSla': 'Tickets viewed on time',
  'overview.work.assignmentSla': 'Tickets assigned on time',
  'overview.work.reopenRate': 'Reopen rate',
  'overview.work.urgentOpen': 'Open urgent and high tickets',
  'overview.work.urgentHigh': '{urgent} urgent · {high} high',
  'overview.work.slaValue': '{percent} of {decided}',
  'overview.work.noSla': 'None decided yet',
  'overview.urgent.title': 'Needs attention now',
  'overview.urgent.overdueInvoices': 'Overdue invoices',
  'overview.urgent.urgentTickets': 'Urgent and high-priority tickets',
  'overview.urgent.expiringContracts': 'Contracts ending soon',
  'overview.urgent.atRiskProjects': 'Projects at high risk',
  'overview.urgent.none': 'Nothing here.',
  'overview.urgent.daysOverdue': '{n} days overdue',
  'overview.urgent.unassigned': 'Unassigned',
  'overview.urgent.endsIn': 'Ends {date} · {n} days left',
  'overview.urgent.allReceivables': 'All receivables',
  'overview.urgent.allContracts': 'All contracts',
  'overview.reason.PAST_EXPECTED_END': 'Past its expected end with work still open',
  'overview.reason.OVERDUE_TASKS': 'Three or more tasks overdue',
  'overview.reason.OVERDUE_SHARE': 'A large share of its dated tasks overdue',
  'overview.reason.OVERDUE_TASK': 'A task overdue',
  'overview.reason.BLOCKED_TASKS': 'Tasks blocked',
  'overview.reason.ON_HOLD': 'On hold',
  'overview.reason.NO_TRACKED_TASKS': 'No tracked tasks',
  'overview.footer': 'Settings version {version} · definitions: work {work}, health {health}, risk {risk}.',

  // ---- Top-line figures -----------------------------------------------------------------------------
  'metric.revenue': 'Cash collected',
  'metric.revenueHint': 'Payments received in the period — Finance’s revenue.',
  'metric.netResult': 'Net result',
  'metric.netResultHint': 'Cash-basis management P&L — not statutory profit.',
  'metric.invoicesIssued': 'Invoices issued',
  'metric.invoicesIssuedHint': 'Issued in the period — not revenue until paid.',
  'metric.operatingExpenses': 'Operating expenses',
  'metric.operatingExpensesHint': 'Finance’s recognised expenses, payroll counted once.',
  'metric.receivables': 'Receivables outstanding',
  'metric.receivablesHint': 'As of {date} · overdue {overdue} ({share})',
  'metric.nothingOwed': 'Nothing owed as of {date}',
  'metric.workCoverage': 'Work-performance coverage',
  'metric.scoredOf': 'of {people} scored',
  'metric.workCoverageHint': '{none} with not enough evidence · median score {median}',
  'metric.atRiskProjects': 'At-risk projects',
  'metric.atRiskHint': 'Of {of} in delivery or support · {watch} to watch',
  'metric.health': 'Company health',
  'metric.healthHint': 'Measurable weight {weight}%',

  // ---- Company health ---------------------------------------------------------------------------------
  'health.title': 'Company health',
  'health.subtitle': 'Four pillars, each from its own evidence; one without enough data is left out — never counted as zero.',
  'health.notEnoughData': 'Not enough reliable data',
  'health.measurableWeight': 'Measurable pillars carry {weight}% of the weight.',
  'health.weight': 'weight {weight}%',
  'health.label.EXCELLENT': 'Excellent',
  'health.label.VERY_GOOD': 'Very good',
  'health.label.GOOD': 'Good',
  'health.label.NEEDS_ATTENTION': 'Needs attention',
  'health.pillar.financial': 'Financial',
  'health.pillar.work': 'Work performance',
  'health.pillar.delivery': 'Delivery',
  'health.pillar.service': 'Service',
  'health.reason.FINANCE_NOT_RELIABLE': 'Finance’s status for the period does not let its figures be leaned on.',
  'health.reason.NO_FINANCIAL_EVIDENCE': 'No financial evidence to score: no margin, receivables or budgets that could be read.',
  'health.reason.TOO_FEW_SCORED_EMPLOYEES': 'Too few employees have a score.',
  'health.reason.TOO_LITTLE_DELIVERY_DATA': 'Too few open project tasks with a deadline, and no project in delivery.',
  'health.reason.TOO_FEW_TICKETS': 'Too few tickets in the period.',
  'health.reason.NO_SLA_EVIDENCE': 'No SLA evidence in the period.',
  'health.reason.NOT_ENOUGH_RELIABLE_DATA': 'The measurable pillars carry too little of the weight for a composite score.',
  'health.component.margin': 'Margin',
  'health.component.collection': 'Not overdue',
  'health.component.budgets': 'Budgets within their limit',
  'health.component.scoredEmployees': 'Average work score',
  'health.component.openOnSchedule': 'Open tasks on schedule',
  'health.component.projectsNotAtRisk': 'Projects not at risk',
  'health.component.viewSla': 'Viewed within SLA',
  'health.component.assignmentSla': 'Assigned within SLA',
  'health.component.notReopened': 'Not reopened',
  'health.componentValue.margin': '{value}% of collected',
  'health.componentValue.collection': 'overdue share {value}%',
  'health.componentValue.budgets': '{value} over their limit',
  'health.componentValue.scoredEmployees': '{value} employees scored',
  'health.componentValue.openOnSchedule': '{value} open dated tasks',
  'health.componentValue.projectsNotAtRisk': '{value} in delivery',
  'health.componentValue.viewSla': '{value}%',
  'health.componentValue.assignmentSla': '{value}%',
  'health.componentValue.notReopened': 'reopen rate {value}%',

  // ---- Risks and positives (executive-health.ts `signals`) -------------------------------------------
  'risk.sectionTitle': 'Risks and positives',
  'risk.rulesNote': 'Fixed rules over the figures above — each one names the figures it read.',
  'risk.title': 'Risks',
  'risk.none': 'No risk rule fired for this period.',
  'risk.unknown': 'Rule {key}',
  'risk.severity.HIGH': 'High',
  'risk.severity.MEDIUM': 'Medium',
  'risk.severity.LOW': 'Low',
  'risk.OVERDUE_GROWING': 'More tasks are falling overdue: {now} this period against {before} the period before.',
  'risk.URGENT_TICKETS_UNASSIGNED': '{count} urgent or high-priority tickets are open with nobody assigned.',
  'risk.SLA_BREACHES_OPEN': 'SLA breaches are open: {unassigned} tickets unassigned past their deadline, {violations} open violations.',
  'risk.Q_TASKS_BLOCKED_LONG': '{count} Q Tasks have been blocked for more than {days} days.',
  'risk.WORKFLOWS_STALLED': '{count} workflows have seen no activity for {days} days.',
  'risk.FINANCE_NOT_VERIFIED': 'Finance’s figures for the period are “{status}”: read them with care.',
  'risk.NEGATIVE_NET_RESULT': 'The net result is negative: {net} (cash-basis management P&L).',
  'risk.EXPENSE_SPIKE': 'Expenses rose more than {threshold}: {now} against {before} the period before.',
  'risk.RECEIVABLES_OVERDUE': '{share} of what clients owe is overdue.',
  'risk.RECEIVABLE_CONCENTRATION': 'One client owes {share} of all receivables.',
  'risk.CONTRACTS_EXPIRING': '{count} contracts end within {days} days.',
  'risk.CONTRACT_PERIODS_NOT_INVOICED': '{count} contract billing periods are due and not invoiced.',
  'risk.SUBSCRIPTIONS_PAST_DUE': '{count} subscriptions are past due.',
  'risk.PROJECTS_AT_RISK': '{count} of {of} projects in delivery are at high delivery risk.',
  'positive.title': 'Positive developments',
  'positive.none': 'No positive rule fired for this period.',
  'positive.OVERDUE_FALLING': 'Fewer tasks fell overdue: {now} this period against {before} the period before.',
  'positive.SLA_STRONG': 'Ticket SLAs are strong: {view} viewed and {assignment} assigned on time.',
  'positive.THROUGHPUT_UP': 'More work was completed: {now} items against {before} the period before.',
  'positive.POSITIVE_NET_RESULT': 'The net result is positive: {net} (cash-basis management P&L).',
  'positive.REVENUE_UP': 'Cash collected rose: {now} against {before} the period before.',

  // ---- Financial Intelligence ---------------------------------------------------------------------------
  'finance.title': 'Financial Intelligence',
  'finance.subtitle': 'Finance’s own figures, read-only — never a second set of books.',
  'finance.tab.overview': 'Overview',
  'finance.tab.trends': 'Trends',
  'finance.tab.receivables': 'Receivables',
  'finance.tab.expenses': 'Expenses',
  'finance.tab.contracts': 'Contracts & subscriptions',
  'finance.tab.trust': 'Trust & coverage',
  'finance.inBase': 'In {currency}, the base currency.',
  'finance.month': 'Month',
  'finance.collected': 'Collected',
  'finance.invoiced': 'Invoiced',
  'finance.outstanding': 'Outstanding',
  'finance.outstandingNow': 'Outstanding now',
  'finance.expenses': 'Expenses',
  'finance.result': 'Result',
  'finance.coverage': 'Coverage',
  'finance.partialMonth': 'Part of the month',
  'finance.asOfBadge': 'As of {date}',
  'finance.asOfDate': 'As of {date}',
  'finance.periodDoesNotApply': 'What stands today — the period chosen above does not change it.',
  'finance.readOnly': 'Read-only: figures are changed in Finance, never here.',
  'finance.card.collected': 'Collected (revenue)',
  'finance.card.collectedHint': 'Cash received in the period — Finance’s revenue.',
  'finance.card.invoiced': 'Invoiced (issued)',
  'finance.card.invoicedHint': '{n} invoices issued — not revenue until paid.',
  'finance.card.operatingExpenses': 'Operating expenses',
  'finance.card.netResult': 'Net result',
  'finance.card.salaries': 'Salaries',
  'finance.card.otherExpenses': 'Other expenses',
  'finance.card.outstanding': 'Outstanding receivables',
  'finance.card.cash': 'Cash',
  'finance.salariesHint': 'Payroll counted once.',
  'finance.cashHint': 'The opening position plus every movement since the cutover, per currency.',
  'finance.cashNone': 'No cash recorded.',
  'finance.months.title': 'The period’s months',
  'finance.coverageSummary.title': 'Coverage',
  'finance.coverageSummary.allClear': 'All {n} months are covered: nothing missing, nothing in doubt.',
  'finance.coverageSummary.notProvided': '{n} months with nothing provided',
  'finance.coverageSummary.overlapChanged': '{n} months may count twice',
  'finance.coverageSummary.missingDocuments': '{n} historical records without a file',
  'finance.coverageSummary.drafts': '{n} historical drafts, not counted',
  'finance.coverageSummary.open': 'Month by month, in Trust & coverage',
  'finance.coverage.COMPLETE': 'Complete',
  'finance.coverage.PARTIAL': 'Partial',
  'finance.coverage.NOT_RECONCILED': 'Not reconciled',
  'finance.coverage.RECORDED': 'Recorded',
  'finance.coverage.LIVE': 'Live records',
  'finance.coverage.HISTORICAL': 'Historical',
  'finance.coverage.HISTORICAL_AND_LIVE': 'Historical and live',
  'finance.coverage.OVERLAP_CHANGED': 'Overlap changed',
  'finance.coverage.NO_RECORDS': 'No records',
  'finance.coverage.NOT_PROVIDED': 'Not provided',
  'finance.statusHint.VERIFIED': 'On a confirmed opening position, and its checks pass.',
  'finance.statusHint.PREVIEW': 'On a cutover still in draft: the opening position may change.',
  'finance.statusHint.UNVERIFIED': 'No opening position at all: only what was recorded.',
  'finance.statusHint.PARTIAL': 'The period reaches before the cutover, or has months with nothing provided.',
  'finance.statusHint.NOT_RECONCILED': 'A check failed: the difference is shown, never absorbed.',
  'finance.statusHint.SETUP_NEEDED': 'Needs a cutover and an opening position first.',
  'finance.statusHint.UNAVAILABLE': 'Before the cutover: nothing reliable is known.',
  'finance.statusHint.RESTRICTED': 'Outside what the dashboard may read.',
  'finance.trends.title': 'Twelve months',
  'finance.trends.subtitle': '{from} to {to}, in {currency}.',
  'finance.trends.table': 'Month by month',
  'finance.recv.aging': 'Aging',
  'finance.recv.bucket.CURRENT': 'Not yet due',
  'finance.recv.bucket.D1_30': '1–30 days overdue',
  'finance.recv.bucket.D31_60': '31–60 days overdue',
  'finance.recv.bucket.D61_90': '61–90 days overdue',
  'finance.recv.bucket.D90_PLUS': 'More than 90 days overdue',
  'finance.recv.overdueCol': 'Overdue',
  'finance.recv.overdueInvoices': '{n} overdue invoices',
  'finance.recv.concentration': '{client} owes {share} of all receivables.',
  'finance.recv.byClient': 'Clients owing most',
  'finance.recv.currencies': 'Billed in',
  'finance.recv.nothingOwed': 'No client owes anything today.',
  'finance.recv.overdueList': 'Overdue invoices',
  'finance.recv.shownOf': 'The {shown} most overdue of {total}',
  'finance.recv.noneOverdue': 'No invoice is overdue.',
  'finance.recv.invoice': 'Invoice',
  'finance.recv.dueDate': 'Due',
  'finance.recv.daysOverdue': 'Days overdue',
  'finance.recv.outstandingOriginal': 'Outstanding (invoice currency)',
  'finance.recv.outstandingBase': 'Outstanding ({currency})',
  'finance.recv.noRate': 'No rate',
  'finance.expected.title': 'Expected cash',
  'finance.expected.subtitle': 'Three separate buckets, never added together — none of it guaranteed.',
  'finance.expected.notGuaranteed': 'Not guaranteed',
  'finance.expected.overdue': 'Overdue, still collectible',
  'finance.expected.overdueHint': 'Already past due — owed, not cash in hand.',
  'finance.expected.scheduled': 'Issued, falling due',
  'finance.expected.scheduledHint': 'Invoices already issued, by due date — scheduled receivables, not revenue.',
  'finance.expected.in7': 'Within 7 days',
  'finance.expected.in30': 'Within 30 days',
  'finance.expected.in90': 'Within 90 days',
  'finance.expected.billing': 'May be billed',
  'finance.expected.billingHint': 'Not invoiced yet — contract periods and subscription renewals that may be billed.',
  'finance.expected.contractsDue': 'Contract periods due, not invoiced',
  'finance.expected.contractsUpcoming': 'Contract periods starting within {days} days',
  'finance.expected.subscriptions': 'Subscription renewals within 30 days ({n})',
  'finance.expected.none': 'None',
  'finance.expected.noRateLeftOut': 'Invoices in {currencies} have no rate: they are left out of these totals.',
  'finance.forecast.title': 'Payment-timing forecast',
  'finance.forecast.estimate': 'An estimate from how invoices were paid — not guaranteed.',
  'finance.forecast.expected30': 'Expected within 30 days',
  'finance.forecast.median': 'Median delay',
  'finance.forecast.p75': '75th percentile',
  'finance.forecast.paidByDue': 'Paid by the due date',
  'finance.forecast.confidence': 'Confidence',
  'finance.forecast.sample': 'Sample',
  'finance.forecast.sampleValue': '{n} invoices paid in full',
  'finance.forecast.window': 'Window',
  'finance.forecast.method': 'Method',
  'finance.forecast.methodText': 'Each outstanding invoice’s due date, moved by the median delay of invoices paid in full in the last year.',
  'finance.forecast.daysLate': '{n} days after the due date',
  'finance.forecast.daysEarly': '{n} days before the due date',
  'finance.forecast.onTheDay': 'On the due date',
  'finance.forecast.notEnough': 'Not enough history for a forecast: {sample} invoices paid in full in the last year, {minimum} needed.',
  'finance.confidence.LOW': 'Low',
  'finance.confidence.MEDIUM': 'Medium',
  'finance.confidence.HIGH': 'High',
  'finance.exp.total': 'Total expenses',
  'finance.exp.historical': 'Of which from historical months: {amount}',
  'finance.exp.split': 'Salaries and the rest',
  'finance.exp.byCategory': 'Other expenses by category',
  'finance.exp.categoriesHidden': 'Not shown while the salaries are hidden: together they would show someone’s pay.',
  'finance.exp.noCategories': 'No other expenses in this period.',
  'finance.exp.uncategorised': 'No category',
  'finance.exp.budgets': 'Budgets against actual',
  'finance.exp.budgetsHint': 'Budgets overlapping the period — each one’s spending over its own dates, in its own currency.',
  'finance.exp.budget': 'Budget',
  'finance.exp.dates': 'Dates',
  'finance.exp.amount': 'Budgeted',
  'finance.exp.spent': 'Spent',
  'finance.exp.used': 'Used',
  'finance.exp.overLimit': 'Over its limit',
  'finance.exp.overThreshold': 'Past its alert threshold',
  'finance.exp.noBudgets': 'No budget overlaps this period.',
  'finance.exp.budgetCount': '{n} budgets',
  'finance.subs.title': 'Subscriptions',
  'finance.subs.mrr': 'MRR',
  'finance.subs.arr': 'ARR',
  'finance.subs.contractedHint': 'Contracted — what running agreements are worth, not cash collected.',
  'finance.subs.contracted': 'Contracted recurring revenue, in each currency.',
  'finance.subs.running': 'Running subscriptions',
  'finance.subs.runningHint': '{active} active · {pastDue} past due · {ending} ending at period end',
  'finance.subs.cycles': 'Billing cycles',
  'finance.subs.cyclesValue': '{monthly} monthly · {yearly} yearly',
  'finance.subs.cancelledHint': '{n} cancelled of {total} ever',
  'finance.subs.byCurrency': 'By currency',
  'finance.subs.subscriptions': 'Subscriptions',
  'finance.subs.none': 'No running subscription.',
  'finance.subs.trend': 'Started and cancelled, by month',
  'finance.subs.trendLabel': 'Subscriptions over {months} months: {started} started, {cancelled} cancelled.',
  'finance.subs.started': 'Started',
  'finance.subs.cancelled': 'Cancelled',
  'finance.subs.billing': 'Billed and collected this year',
  'finance.subs.noBilling': 'No subscription invoice this year.',
  'finance.subs.invoices': '{n} invoices',
  'finance.subs.payments': '{n} payments',
  'finance.subs.collectedBase': 'Collected, in the base currency at each payment’s own rate: {amount}',
  'finance.subs.aging': 'Subscription invoices owed, by age',
  'finance.subs.agingHint': 'In each currency — never added across currencies.',
  'finance.subs.nothingOwed': 'No subscription invoice is owed.',
  'finance.subs.bucket.current': 'Not yet due',
  'finance.subs.bucket.d1_30': '1–30 days',
  'finance.subs.bucket.d31_60': '31–60 days',
  'finance.subs.bucket.d61_90': '61–90 days',
  'finance.subs.bucket.d90_plus': 'Over 90 days',
  'finance.subs.upcoming': 'Renewals in the next 30 days',
  'finance.subs.upcomingHint': 'Billing dates ahead — invoices not issued yet.',
  'finance.subs.noUpcoming': 'No renewal in the next 30 days.',
  'finance.subs.subscription': 'Subscription',
  'finance.subs.nextBilling': 'Next billing',
  'finance.subs.price': 'Net price',
  'finance.contracts.title': 'Contracts',
  'finance.contracts.notCollected': 'Contracted values here are agreements, not money collected: MRR, ARR and a contract’s face value are not revenue.',
  'finance.contracts.faceValue': 'A contract’s face value is what it is worth over its term — not revenue, and not cash.',
  'finance.contracts.active': 'Running today',
  'finance.contracts.waiting': 'Waiting to start',
  'finance.contracts.expiring': 'Ending within {days} days',
  'finance.contracts.dueTotal': 'Due, not invoiced',
  'finance.contracts.dueHint': 'Contract periods begun and not invoiced — potential invoices, not cash.',
  'finance.contracts.expiringList': 'Contracts ending soon',
  'finance.contracts.noneExpiring': 'No contract ends within {days} days.',
  'finance.contracts.contract': 'Contract',
  'finance.contracts.ends': 'Ends',
  'finance.contracts.daysLeft': 'Days left',
  'finance.contracts.billing': 'Billing periods',
  'finance.contracts.billingHint': 'Due and not invoiced, and starting within {days} days — potential invoices, not cash.',
  'finance.contracts.upcomingTotal': 'Starting within {days} days',
  'finance.contracts.noPeriods': 'No billing period is due or coming up.',
  'finance.contracts.period': 'Period',
  'finance.contracts.stateDue': 'Due, not invoiced',
  'finance.contracts.stateUpcoming': 'Upcoming',
  'finance.trust.statusLabel': 'Finance status',
  'finance.trust.statusTitle': 'Report status',
  'finance.trust.comparison': 'The period compared with:',
  'finance.trust.generated': 'Computed',
  'finance.trust.timezone': 'Company time zone',
  'finance.trust.legend': 'What each status means',
  'finance.trust.cutover': 'Cutover',
  'finance.trust.cutoverDate': 'Date',
  'finance.trust.cutoverStatus': 'Status',
  'finance.trust.confirmedAt': 'Confirmed',
  'finance.trust.standing': 'The period and the cutover',
  'finance.trust.cash': 'Cash by currency',
  'finance.trust.basis': 'What is counted, and when',
  'finance.trust.fx': 'Currency conversion',
  'finance.trust.notices': 'Notices',
  'finance.trust.noNotices': 'No notices for this period.',
  'finance.trust.monthCoverage': 'Coverage by month',
  'finance.trust.drafts': 'Drafts',
  'finance.trust.missingDocuments': 'Without a file',
  'finance.cutover.NONE': 'None',
  'finance.cutover.DRAFT': 'Draft',
  'finance.cutover.CONFIRMED': 'Confirmed',
  'finance.standing.AFTER_CUTOVER': 'After the cutover',
  'finance.standing.SPANS_CUTOVER': 'Spans the cutover',
  'finance.standing.BEFORE_CUTOVER': 'Before the cutover',
  'finance.standing.NO_CUTOVER': 'No confirmed cutover',
  'finance.basis.revenue': 'Revenue',
  'finance.basis.expenses': 'Expenses',
  'finance.basis.payroll': 'Payroll',
  'finance.basis.pointsCash': 'Points paid in cash',
  'finance.basis.historical': 'Historical months',
  'finance.basis.balances': 'Balances',
  'finance.basisValue.CASH_RECEIVED': 'Cash received',
  'finance.basisValue.APPROVED_ON_EXPENSE_DATE': 'Approved, on the expense’s date',
  'finance.basisValue.PAID_PAYROLL_ONCE_ON_MONTH_END': 'Paid payroll, once, on the month’s last day',
  'finance.basisValue.PAID_BY_HAND_ON_THE_DAY': 'Paid by hand, on the day',
  'finance.basisValue.FINALIZED_MONTH_ON_ITS_LAST_DAY': 'A finalized month, on its last day',
  'finance.basisValue.OPENING_POSITION_PLUS_MOVEMENTS': 'The opening position plus movements',
  'finance.fx.payments': 'Payments',
  'finance.fx.historical': 'Historical months',
  'finance.fx.partners': 'Partners',
  'finance.fx.expenses': 'Expenses',
  'finance.fx.balances': 'Balances',
  'finance.fxValue.RATE_RECORDED_WITH_THE_PAYMENT': 'The rate recorded with the payment',
  'finance.fxValue.RATE_TYPED_FOR_THE_MONTH': 'The rate entered for the month',
  'finance.fxValue.RATE_RECORDED_WITH_THE_TRANSACTION': 'The rate recorded with the transaction',
  'finance.fxValue.TODAY_RATE_FLAGGED': 'Today’s rate, flagged',
  'finance.fxValue.ORIGINAL_CURRENCY_ONLY': 'Their own currency only',

  // ---- Finance's notices (financial-reports.service.ts) ----------------------------------------------
  'notice.NO_CONFIRMED_CUTOVER': 'No confirmed cutover: the figures are what was recorded, not checked against an opening position.',
  'notice.PERIOD_BEFORE_CUTOVER': 'The period reaches before the cutover: what came before it is only partly known.',
  'notice.HISTORICAL_RESTRICTED': 'Historical months are outside what may be read here: they are left out.',
  'notice.MONTHS_NOT_PROVIDED': 'Some months have nothing provided for salaries or expenses — unknown, never zero.',
  'notice.OVERLAP_CHANGED': 'Live records changed after a historical month was reviewed: that month may count twice.',
  'notice.UNCONVERTED': 'Some amounts have no exchange rate: they are left out of the base-currency totals.',
  'notice.EXPENSES_AT_TODAY_RATE': 'Some expenses in other currencies are valued at today’s rate.',
  'notice.REVENUE_INCLUDES_TAX': 'Collected revenue includes the tax on some invoices.',
  'notice.PARTIAL_MONTH': 'The period ends before a month’s last day: that month’s monthly totals are not in it yet.',
  'notice.NO_CUTOVER': 'Needs a cutover and an opening position first.',
  'notice.BEFORE_CUTOVER': 'The date is before the cutover: nothing reliable is known.',
  'notice.CUTOVER_DRAFT': 'The cutover is still a draft: the opening position may change.',
  'notice.detail.months': 'Months:',
  'notice.detail.cutover': 'Cutover:',
  'notice.detail.currencies': 'Currencies:',
  'notice.detail.invoices': 'Invoices:',

  // ---- AI Insights -------------------------------------------------------------------------------------
  'insights.title': 'AI Insights',
  'insights.subtitle': 'AI’s briefings on the stored reports — labelled as AI’s, citing the figures they read, never a source of figures.',
  'insights.availability': 'Availability',
  'insights.state.ENABLED': 'Enabled',
  'insights.state.DISABLED': 'Switched off',
  'insights.state.NOT_CONFIGURED': 'Not set up',
  'insights.state.BUDGET_EXCEEDED': 'Budget spent',
  'insights.stateHint.ENABLED': 'Briefings are written for new reports, within this month’s budget.',
  'insights.stateHint.DISABLED': 'AI explanations are switched off (on the server or in the admin’s settings): nothing is sent.',
  'insights.stateHint.NOT_CONFIGURED': 'AI is switched on but not set up on the server: there is no model to ask.',
  'insights.stateHint.BUDGET_EXCEEDED': 'This month’s token budget is spent: briefings wait until next month.',
  'insights.usage': 'This month’s usage',
  'insights.tokens': '{used} of {budget} tokens',
  'insights.cost': 'Estimated cost',
  'insights.costEstimate': 'An estimate from the prices in the settings — not billing.',
  'insights.costEstimateAsOf': 'An estimate from the prices dated {date} in the settings — not billing.',
  'insights.noCost': 'No estimate: no prices are set.',
  'insights.model': 'Model',
  'insights.list': 'Briefings',
  'insights.none': 'No briefing yet',
  'insights.noneHint': 'Briefings are written for the daily, weekly and monthly reports once AI is enabled.',
  'insights.noneHintEnabled': 'The next scheduled report will have one.',
  'insights.briefing': 'Briefing',
  'insights.kindTitle.DAILY': 'Daily briefing',
  'insights.kindTitle.WEEKLY': 'Weekly briefing',
  'insights.kindTitle.MONTHLY': 'Monthly report',
  'insights.kindTitle.CUSTOM': 'Custom-range report',
  'insights.revision': 'Explanation revision {n}',
  'insights.openReport': 'Open the report',
  'insights.regenerate': 'Regenerate',
  'insights.regenerated': 'A new explanation was asked for: it appears here once written.',
  'aiAct.ask': 'Ask AI to explain',
  'aiAct.tryAgain': 'Try again',
  'aiAct.regenerate': 'Regenerate',
  'aiAct.regenerateTitle': 'Ask AI again?',
  'aiAct.regenerateBody': 'AI writes a new explanation from the same saved figures, and that uses tokens. The saved one stays in the history, and nothing here asks again by itself.',
  'aiAct.regenerateConfirm': 'Regenerate',
  'aiAct.requested': 'AI was asked: the explanation appears here once written, and is saved from then on.',
  'aiAct.reused': 'The saved explanation — AI was not asked again.',
  'aiAct.inProgress': 'Already being written — it appears here once ready.',
  'aiAct.unavailable': 'AI explanations are switched off or not set up.',
  'aiAct.writtenFor': 'Written for revision {revision} and kept as saved — Regenerate to ask AI about this one.',
  'aiAct.writtenForOnly': 'Written for revision {revision} of this report, and kept as saved.',
  'aiAct.savedFirst': 'Saved first: an explanation is asked of AI once and saved. Opening it again, or asking again, never calls AI — only Regenerate does.',
  'reports.recalculate': 'Recalculate',
  'reports.recalculateTitle': 'Read this period again?',
  'reports.recalculateBody': 'The period is read again from its records. If they changed, a new revision is saved and this one is kept; if not, nothing changes. AI’s saved explanation is not rewritten.',
  'reports.recalculateConfirm': 'Recalculate',
  'reports.recalculated': 'Recalculated — revision {revision} saved; the older one is kept.',
  'reports.recalculateUnchanged': 'Nothing changed — the saved report stands.',
  'reports.snapshot.reused': 'This period is already saved — its saved report is shown; nothing was computed again.',
  'insights.itemTokens': 'Tokens: {input} in, {output} out',
  'insights.itemCost': 'Estimated cost: {cost}',
  'insights.failure': 'Reason:',

  // ---- Executive Reports ---------------------------------------------------------------------------------
  'reports.title': 'Executive Reports',
  'reports.subtitle': 'Stored snapshots of days, weeks, months and ranges — never overwritten: a change in their sources makes a new revision.',
  'reports.kind.DAILY': 'Daily',
  'reports.kind.WEEKLY': 'Weekly',
  'reports.kind.MONTHLY': 'Monthly',
  'reports.kind.CUSTOM': 'Custom range',
  'reports.filter.kind': 'Kind',
  'reports.filter.since': 'Starting on or after',
  'reports.filter.until': 'Ending on or before',
  'reports.col.period': 'Period',
  'reports.col.kind': 'Kind',
  'reports.col.revision': 'Revision',
  'reports.col.generatedBy': 'Made by',
  'reports.col.computed': 'Computed',
  'reports.col.finalized': 'Finalized',
  'reports.col.ai': 'AI',
  'reports.col.coverage': 'Coverage',
  'reports.generatedBy.SYSTEM': 'System (scheduled)',
  'reports.generatedBy.MANUAL': 'Manual',
  'reports.notFinalized': 'Not yet',
  'reports.revisionShort': 'Revision {n}',
  'reports.none': 'No report yet',
  'reports.noneHint': 'Reports are made on a schedule — each day, week and month — or here, with Make a snapshot.',
  'reports.noneFiltered': 'No report matches',
  'reports.coverage.people': '{scored} scored · {none} not enough evidence',
  'reports.coverage.health': 'Health weight {weight}%',
  'reports.coverage.truncated': 'Evidence beyond the read limit',
  'reports.coverage.notices': '{n} notices',
  'reports.aiStatus.READY': 'Ready',
  'reports.aiStatus.PENDING': 'Waiting',
  'reports.aiStatus.RUNNING': 'Being written',
  'reports.aiStatus.FAILED': 'Failed',
  'reports.aiStatus.DISABLED': 'Switched off',
  'reports.aiStatus.NOT_CONFIGURED': 'Not set up',
  'reports.aiStatus.BUDGET_EXCEEDED': 'Budget spent',
  'reports.aiStatus.NOT_REQUESTED': 'None yet',
  'reports.aiStatus.ENABLED': 'Enabled',
  'reports.snapshot.title': 'Make a snapshot',
  'reports.snapshot.hint': 'Stores a period as computed now. The same sources give the same report; changed sources make a new revision.',
  'reports.snapshot.kind': 'Kind',
  'reports.snapshot.day': 'Day',
  'reports.snapshot.dayInWeek': 'Any day of the week',
  'reports.snapshot.month': 'Month',
  'reports.snapshot.submit': 'Make the snapshot',
  'reports.snapshot.saving': 'Computing…',
  'reports.snapshot.saved': 'Report snapshot saved — revision {revision}, {status}.',
  'reports.snapshot.open': 'Open the snapshot (revision {revision})',
  'reports.snapshot.note': 'Only the dashboard’s own record is written — nothing in the business changes.',
  'reports.back': 'All reports',
  'reports.exportFailed': 'The export could not be made: {message}',
  'reports.detail.title': '{kind} report',
  'reports.detail.superseded': 'Superseded',
  'reports.detail.notCurrent': 'A later revision supersedes this one.',
  'reports.detail.openCurrent': 'Open revision {n}',
  'reports.detail.period': 'Period',
  'reports.detail.timezone': 'Time zone',
  'reports.detail.generatedBy': 'Made by',
  'reports.detail.requestedBy': 'Asked for by {name}',
  'reports.detail.versions': 'Versions',
  'reports.detail.settingsVersion': 'Settings version {v}',
  'reports.detail.liveWhenComputed': 'The period was still running when this was computed: its figures stop at {time}.',
  'reports.detail.storedNote': 'Everything below is the report as stored — not computed again. Names are read now; a removed account shows as a former employee.',
  'reports.detail.revisions': 'Revisions',
  'reports.detail.current': 'Current',
  'reports.detail.viewing': 'Shown',
  'reports.detail.reason.SOURCES_CHANGED': 'Its sources changed',
  'reports.detail.unreadable': 'This report’s stored figures are in a shape this page cannot read. Its exports still hold them.',
  'reports.detail.figures': 'The stored figures',
  'reports.detail.figuresHint': 'As computed at {time}. Links open the same period, read live.',
  'reports.detail.ai': 'AI explanation of this report',
  'reports.detail.months': 'Finance by month',
  'reports.detail.people': 'Work performance',
  'reports.detail.peopleHint': '{people} people · {scored} scored · {none} with not enough evidence',
  'reports.detail.noPeople': 'Nobody in this report.',
  'reports.detail.score': 'Score',
  'reports.detail.confidence': 'Confidence',
  'reports.detail.evidence': 'Evidence',
  'reports.detail.measurableWeight': 'Measurable weight',
  'reports.detail.peopleNote': 'A work-performance score from attributed work evidence — not points, pay or an HR decision; no attendance, shifts or leave go into it.',
  'reports.detail.urgentAsComputed': 'What needed attention when the report was computed ({time}).',
  'reports.confidence.HIGH': 'High',
  'reports.confidence.MEDIUM': 'Medium',
  'reports.confidence.LOW': 'Low',
  'reports.confidence.NONE': 'None',
} as const;

export const areaAAr: Record<keyof typeof areaAEn, string> = {
  // ---- النظرة التنفيذية ------------------------------------------------------------------------------
  'overview.title': 'النظرة التنفيذية',
  'overview.subtitle': 'نظرة سريعة على الشركة في هذه الفترة: أرقام المالية كما هي، والعمل، والأفراد، والتسليم، وصحة الشركة.',
  'overview.snapshot': 'تقرير محفوظ · النسخة {revision}',
  'overview.noSnapshot': 'هذه الأرقام محسوبة الآن، ولا يوجد تقرير محفوظ لهذه الفترة بعد.',
  'overview.toReports': 'أنشئ تقريرًا من «التقارير التنفيذية»',
  'overview.ai.title': 'ملخص الذكاء الاصطناعي لهذه الفترة',
  'overview.ai.latestDaily': 'أحدث ملخص يومي — {date}',
  'overview.ai.openReport': 'فتح التقرير',
  'overview.ai.allInsights': 'كل رؤى الذكاء الاصطناعي',
  'overview.ai.notThisPeriod': 'لا يوجد ملخص خاص بهذه الفترة: {reason}',
  'overview.chart.asTable': 'عرض الأرقام في جدول',
  'overview.chart.trendLabel': 'مخطط: المحصّل والمصروفات والنتيجة شهريًا، من {from} إلى {to}، بعملة {currency}.',
  'overview.chart.gaps': 'أشهر غير معروفة تظهر فارغة: {months}',
  'overview.chart.faded': 'أشهر لم تؤكدها المالية بالكامل تظهر باهتة: {months}',
  'overview.trend.title': 'المالية شهريًا',
  'overview.trend.subtitle': 'اثنا عشر شهرًا حتى آخر شهر في الفترة، بعملة {currency}.',
  'overview.trend.open': 'فتح الاتجاهات',
  'overview.work.title': 'العمل في هذه الفترة',
  'overview.work.subtitle': 'مقارنةً بـ{basis}، مع احتساب كل عنصر عمل مرة واحدة.',
  'overview.work.open': 'العمل والإنتاجية',
  'overview.work.now': 'هذه الفترة',
  'overview.work.before': 'الفترة السابقة',
  'overview.work.completed': 'العناصر المنجزة',
  'overview.work.completedHint': 'العناصر المنجزة هي العناصر الرئيسية: المهام خارج سير العمل، وسير العمل المكتمل. ونحسب كل منها مرة واحدة.',
  'overview.work.created': 'المهام المنشأة',
  'overview.work.ticketsCreated': 'التذاكر المنشأة',
  'overview.work.steps': 'خطوات سير العمل المنجزة',
  'overview.work.resolved': 'التذاكر المحلولة',
  'overview.work.overdueNow': 'المهام المتأخرة الآن',
  'overview.work.dueSoon': 'مهام موعد تسليمها خلال يومين',
  'overview.work.viewSla': 'الاطلاع على التذاكر في الوقت',
  'overview.work.assignmentSla': 'إسناد التذاكر في الوقت',
  'overview.work.reopenRate': 'معدل إعادة الفتح',
  'overview.work.urgentOpen': 'التذاكر المفتوحة العاجلة والعالية',
  'overview.work.urgentHigh': 'عاجلة {urgent} · عالية {high}',
  'overview.work.slaValue': '{percent} من {decided}',
  'overview.work.noSla': 'لم يتم حسم أي منها بعد',
  'overview.urgent.title': 'يحتاج إلى اهتمام الآن',
  'overview.urgent.overdueInvoices': 'الفواتير المتأخرة',
  'overview.urgent.urgentTickets': 'التذاكر العاجلة وعالية الأولوية',
  'overview.urgent.expiringContracts': 'عقود تنتهي قريبًا',
  'overview.urgent.atRiskProjects': 'مشاريع معرضة لخطر كبير',
  'overview.urgent.none': 'لا شيء هنا.',
  'overview.urgent.daysOverdue': 'أيام التأخر: {n}',
  'overview.urgent.unassigned': 'غير مسندة',
  'overview.urgent.endsIn': 'ينتهي في {date} · الأيام المتبقية: {n}',
  'overview.urgent.allReceivables': 'كل المستحقات',
  'overview.urgent.allContracts': 'كل العقود',
  'overview.reason.PAST_EXPECTED_END': 'تجاوز موعد انتهائه المتوقع وما زال فيه عمل مفتوح',
  'overview.reason.OVERDUE_TASKS': 'ثلاث مهام متأخرة أو أكثر',
  'overview.reason.OVERDUE_SHARE': 'نسبة كبيرة من مهامه التي لها موعد تسليم متأخرة',
  'overview.reason.OVERDUE_TASK': 'مهمة متأخرة',
  'overview.reason.BLOCKED_TASKS': 'مهام معلّقة',
  'overview.reason.ON_HOLD': 'متوقف',
  'overview.reason.NO_TRACKED_TASKS': 'لا توجد مهام متتبعة',
  'overview.footer': 'نسخة الإعدادات {version} · التعريفات: العمل {work}، الصحة {health}، المخاطر {risk}.',

  // ---- الأرقام الرئيسية -----------------------------------------------------------------------------
  'metric.revenue': 'النقد المحصّل',
  'metric.revenueHint': 'الدفعات المستلمة في الفترة، وهي ما تعتبره المالية إيرادات.',
  'metric.netResult': 'صافي النتيجة',
  'metric.netResultHint': 'من قائمة دخل إدارية على الأساس النقدي، وليس الربح المحاسبي الرسمي.',
  'metric.invoicesIssued': 'الفواتير الصادرة',
  'metric.invoicesIssuedHint': 'صدرت في الفترة، ولا تصبح إيرادًا إلا بعد دفعها.',
  'metric.operatingExpenses': 'المصروفات التشغيلية',
  'metric.operatingExpensesHint': 'المصروفات المعترف بها في المالية، والرواتب محتسبة مرة واحدة.',
  'metric.receivables': 'المستحقات القائمة',
  'metric.receivablesHint': 'حتى {date} · المتأخر {overdue} ({share})',
  'metric.nothingOwed': 'لا توجد مستحقات حتى {date}',
  'metric.workCoverage': 'تغطية تقييم الأداء',
  'metric.scoredOf': 'من أصل {people} تم تقييمهم',
  'metric.workCoverageHint': 'دون أدلة كافية {none} · الدرجة الوسيطة {median}',
  'metric.atRiskProjects': 'المشاريع المعرضة للخطر',
  'metric.atRiskHint': 'من {of} قيد التسليم أو الدعم · تحت المراقبة {watch}',
  'metric.health': 'صحة الشركة',
  'metric.healthHint': 'الوزن القابل للقياس {weight}%',

  // ---- صحة الشركة -----------------------------------------------------------------------------------
  'health.title': 'صحة الشركة',
  'health.subtitle': 'أربعة محاور، لكل محور أدلته الخاصة. المحور الذي ليس له بيانات كافية نستبعده، ولا نحسبه صفرًا.',
  'health.notEnoughData': 'لا توجد بيانات موثوقة كافية',
  'health.measurableWeight': 'المحاور القابلة للقياس تحمل {weight}% من الوزن.',
  'health.weight': 'الوزن {weight}%',
  'health.label.EXCELLENT': 'ممتاز',
  'health.label.VERY_GOOD': 'جيد جدًا',
  'health.label.GOOD': 'جيد',
  'health.label.NEEDS_ATTENTION': 'يحتاج إلى اهتمام',
  'health.pillar.financial': 'المالي',
  'health.pillar.work': 'أداء العمل',
  'health.pillar.delivery': 'التسليم',
  'health.pillar.service': 'الخدمة',
  'health.reason.FINANCE_NOT_RELIABLE': 'حالة المالية لهذه الفترة لا تسمح بالاعتماد على أرقامها.',
  'health.reason.NO_FINANCIAL_EVIDENCE': 'لا توجد أدلة مالية للتقييم: لا هامش ولا مستحقات ولا ميزانيات يمكن قراءتها.',
  'health.reason.TOO_FEW_SCORED_EMPLOYEES': 'عدد الموظفين الحاصلين على درجة قليل جدًا.',
  'health.reason.TOO_LITTLE_DELIVERY_DATA': 'المهام المفتوحة ذات موعد التسليم في المشاريع قليلة جدًا، ولا يوجد مشروع قيد التسليم.',
  'health.reason.TOO_FEW_TICKETS': 'عدد التذاكر في هذه الفترة قليل جدًا.',
  'health.reason.NO_SLA_EVIDENCE': 'لا توجد بيانات عن المهل في هذه الفترة.',
  'health.reason.NOT_ENOUGH_RELIABLE_DATA': 'وزن المحاور القابلة للقياس أقل من أن نحسب منه درجة إجمالية.',
  'health.component.margin': 'هامش الربح',
  'health.component.collection': 'غير المتأخر',
  'health.component.budgets': 'الميزانيات ضمن حدودها',
  'health.component.scoredEmployees': 'متوسط درجة العمل',
  'health.component.openOnSchedule': 'المهام المفتوحة في موعدها',
  'health.component.projectsNotAtRisk': 'المشاريع غير المعرضة للخطر',
  'health.component.viewSla': 'الاطلاع ضمن المهلة',
  'health.component.assignmentSla': 'الإسناد ضمن المهلة',
  'health.component.notReopened': 'دون إعادة فتح',
  'health.componentValue.margin': '{value}% من المحصّل',
  'health.componentValue.collection': 'نسبة المتأخر {value}%',
  'health.componentValue.budgets': 'تجاوزت حدها: {value}',
  'health.componentValue.scoredEmployees': 'موظفون لهم درجة: {value}',
  'health.componentValue.openOnSchedule': 'مهام مفتوحة لها موعد تسليم: {value}',
  'health.componentValue.projectsNotAtRisk': 'قيد التسليم: {value}',
  'health.componentValue.viewSla': '{value}%',
  'health.componentValue.assignmentSla': '{value}%',
  'health.componentValue.notReopened': 'معدل إعادة الفتح {value}%',

  // ---- المخاطر والإيجابيات ---------------------------------------------------------------------------
  'risk.sectionTitle': 'المخاطر والإيجابيات',
  'risk.rulesNote': 'قواعد ثابتة نطبقها على الأرقام أعلاه، وكل قاعدة تذكر الأرقام التي قرأتها.',
  'risk.title': 'المخاطر',
  'risk.none': 'لم تنطبق أي قاعدة مخاطر على هذه الفترة.',
  'risk.unknown': 'القاعدة {key}',
  'risk.severity.HIGH': 'عالية',
  'risk.severity.MEDIUM': 'متوسطة',
  'risk.severity.LOW': 'منخفضة',
  'risk.OVERDUE_GROWING': 'يزيد عدد المهام التي تأخرت عن موعدها: {now} في هذه الفترة مقابل {before} في الفترة السابقة.',
  'risk.URGENT_TICKETS_UNASSIGNED': 'تذاكر مفتوحة عاجلة أو عالية الأولوية بدون مسؤول: {count}.',
  'risk.SLA_BREACHES_OPEN': 'توجد تجاوزات مهل مفتوحة: تذاكر غير مسندة تجاوزت مهلتها ({unassigned})، ومخالفات مفتوحة ({violations}).',
  'risk.Q_TASKS_BLOCKED_LONG': 'مهام الطابور المعلّقة منذ أكثر من {days} أيام: {count}.',
  'risk.WORKFLOWS_STALLED': 'سير عمل بلا أي نشاط منذ {days} أيام: {count}.',
  'risk.FINANCE_NOT_VERIFIED': 'أرقام المالية لهذه الفترة «{status}»: اقرأها بحذر.',
  'risk.NEGATIVE_NET_RESULT': 'صافي النتيجة سالب: {net} (قائمة دخل إدارية على الأساس النقدي).',
  'risk.EXPENSE_SPIKE': 'ارتفعت المصروفات بأكثر من {threshold}: {now} مقابل {before} في الفترة السابقة.',
  'risk.RECEIVABLES_OVERDUE': '{share} من المبالغ المستحقة على العملاء متأخرة السداد.',
  'risk.RECEIVABLE_CONCENTRATION': 'عميل واحد يدين بـ{share} من كل المستحقات.',
  'risk.CONTRACTS_EXPIRING': 'عقود تنتهي خلال {days} يومًا: {count}.',
  'risk.CONTRACT_PERIODS_NOT_INVOICED': 'فترات فوترة في العقود مستحقة ولم تصدر فواتيرها: {count}.',
  'risk.SUBSCRIPTIONS_PAST_DUE': 'اشتراكات متأخرة السداد: {count}.',
  'risk.PROJECTS_AT_RISK': 'مشاريع قيد التسليم معرضة لخطر كبير في تسليمها: {count} من {of}.',
  'positive.title': 'تطورات إيجابية',
  'positive.none': 'لم تنطبق أي قاعدة إيجابية على هذه الفترة.',
  'positive.OVERDUE_FALLING': 'قلّ عدد المهام التي تأخرت عن موعدها: {now} في هذه الفترة مقابل {before} في الفترة السابقة.',
  'positive.SLA_STRONG': 'التذاكر ملتزمة بالمهل: تم الاطلاع على {view} منها وإسناد {assignment} في الوقت.',
  'positive.THROUGHPUT_UP': 'زاد عدد العناصر المنجزة: {now} مقابل {before} في الفترة السابقة.',
  'positive.POSITIVE_NET_RESULT': 'صافي النتيجة موجب: {net} (قائمة دخل إدارية على الأساس النقدي).',
  'positive.REVENUE_UP': 'ارتفع النقد المحصّل: {now} مقابل {before} في الفترة السابقة.',

  // ---- الذكاء المالي --------------------------------------------------------------------------------
  'finance.title': 'الذكاء المالي',
  'finance.subtitle': 'أرقام المالية كما هي، للقراءة فقط، وليست دفاتر حسابات ثانية.',
  'finance.tab.overview': 'نظرة عامة',
  'finance.tab.trends': 'الاتجاهات',
  'finance.tab.receivables': 'المستحقات',
  'finance.tab.expenses': 'المصروفات',
  'finance.tab.contracts': 'العقود والاشتراكات',
  'finance.tab.trust': 'الموثوقية والتغطية',
  'finance.inBase': 'بعملة {currency}، وهي العملة الأساسية.',
  'finance.month': 'الشهر',
  'finance.collected': 'المحصّل',
  'finance.invoiced': 'المفوتر',
  'finance.outstanding': 'غير المسدد',
  'finance.outstandingNow': 'غير المسدد الآن',
  'finance.expenses': 'المصروفات',
  'finance.result': 'النتيجة',
  'finance.coverage': 'التغطية',
  'finance.partialMonth': 'جزء من الشهر',
  'finance.asOfBadge': 'حتى {date}',
  'finance.asOfDate': 'حتى {date}',
  'finance.periodDoesNotApply': 'الوضع كما هو اليوم، ولا تغيّره الفترة المختارة أعلاه.',
  'finance.readOnly': 'للقراءة فقط: تعديل الأرقام يتم في المالية، وليس هنا.',
  'finance.card.collected': 'المحصّل (الإيرادات)',
  'finance.card.collectedHint': 'النقد المستلم في الفترة، وهو ما تعتبره المالية إيرادات.',
  'finance.card.invoiced': 'المفوتر (الصادر)',
  'finance.card.invoicedHint': 'الفواتير الصادرة: {n}. لا تصبح إيرادًا إلا بعد دفعها.',
  'finance.card.operatingExpenses': 'المصروفات التشغيلية',
  'finance.card.netResult': 'صافي النتيجة',
  'finance.card.salaries': 'الرواتب',
  'finance.card.otherExpenses': 'مصروفات أخرى',
  'finance.card.outstanding': 'المستحقات القائمة',
  'finance.card.cash': 'النقد',
  'finance.salariesHint': 'الرواتب محتسبة مرة واحدة.',
  'finance.cashHint': 'المركز الافتتاحي مضافًا إليه كل الحركات منذ تاريخ الانتقال، لكل عملة.',
  'finance.cashNone': 'لا يوجد نقد مسجّل.',
  'finance.months.title': 'أشهر الفترة',
  'finance.coverageSummary.title': 'التغطية',
  'finance.coverageSummary.allClear': 'كل الأشهر مغطاة ({n}): لا شيء ناقص ولا شيء مشكوك فيه.',
  'finance.coverageSummary.notProvided': 'أشهر بلا بيانات: {n}',
  'finance.coverageSummary.overlapChanged': 'أشهر قد يتكرر حسابها: {n}',
  'finance.coverageSummary.missingDocuments': 'سجلات تاريخية دون ملف: {n}',
  'finance.coverageSummary.drafts': 'مسودات تاريخية غير محتسبة: {n}',
  'finance.coverageSummary.open': 'شهرًا بشهر في «الموثوقية والتغطية»',
  'finance.coverage.COMPLETE': 'مكتمل',
  'finance.coverage.PARTIAL': 'جزئي',
  'finance.coverage.NOT_RECONCILED': 'غير مطابق',
  'finance.coverage.RECORDED': 'مسجّل',
  'finance.coverage.LIVE': 'سجلات حية',
  'finance.coverage.HISTORICAL': 'تاريخي',
  'finance.coverage.HISTORICAL_AND_LIVE': 'تاريخي وحي',
  'finance.coverage.OVERLAP_CHANGED': 'تغيّر التداخل',
  'finance.coverage.NO_RECORDS': 'لا سجلات',
  'finance.coverage.NOT_PROVIDED': 'لم يتم إدخاله',
  'finance.statusHint.VERIFIED': 'مبني على رصيد افتتاحي مؤكد، وفحوصه ناجحة.',
  'finance.statusHint.PREVIEW': 'مبني على تاريخ تحول ما زال مسودة: قد يتغير الرصيد الافتتاحي.',
  'finance.statusHint.UNVERIFIED': 'لا يوجد رصيد افتتاحي أبدًا: الأرقام هي ما تم تسجيله فقط.',
  'finance.statusHint.PARTIAL': 'تمتد الفترة إلى ما قبل تاريخ الانتقال، أو فيها أشهر بلا بيانات.',
  'finance.statusHint.NOT_RECONCILED': 'فشل أحد الفحوص: نعرض الفرق كما هو ولا نخفيه.',
  'finance.statusHint.SETUP_NEEDED': 'يجب تحديد تاريخ الانتقال والمركز الافتتاحي أولًا.',
  'finance.statusHint.UNAVAILABLE': 'قبل تاريخ الانتقال: لا توجد معلومات موثوقة.',
  'finance.statusHint.RESTRICTED': 'ليست لدى اللوحة صلاحية لقراءة هذا.',
  'finance.trends.title': 'اثنا عشر شهرًا',
  'finance.trends.subtitle': 'من {from} إلى {to}، بعملة {currency}.',
  'finance.trends.table': 'شهرًا بشهر',
  'finance.recv.aging': 'أعمار الديون',
  'finance.recv.bucket.CURRENT': 'لم تستحق بعد',
  'finance.recv.bucket.D1_30': 'متأخرة 1–30 يومًا',
  'finance.recv.bucket.D31_60': 'متأخرة 31–60 يومًا',
  'finance.recv.bucket.D61_90': 'متأخرة 61–90 يومًا',
  'finance.recv.bucket.D90_PLUS': 'متأخرة أكثر من 90 يومًا',
  'finance.recv.overdueCol': 'المتأخر',
  'finance.recv.overdueInvoices': 'فواتير متأخرة: {n}',
  'finance.recv.concentration': '{client} يدين بـ{share} من كل المستحقات.',
  'finance.recv.byClient': 'العملاء الأكثر مديونية',
  'finance.recv.currencies': 'عملات الفوترة',
  'finance.recv.nothingOwed': 'لا يدين أي عميل بشيء اليوم.',
  'finance.recv.overdueList': 'الفواتير المتأخرة',
  'finance.recv.shownOf': 'الأكثر تأخرًا: {shown} من {total}',
  'finance.recv.noneOverdue': 'لا توجد فاتورة متأخرة.',
  'finance.recv.invoice': 'الفاتورة',
  'finance.recv.dueDate': 'تاريخ الاستحقاق',
  'finance.recv.daysOverdue': 'أيام التأخر',
  'finance.recv.outstandingOriginal': 'غير المسدد (بعملة الفاتورة)',
  'finance.recv.outstandingBase': 'غير المسدد ({currency})',
  'finance.recv.noRate': 'لا يوجد سعر صرف',
  'finance.expected.title': 'النقد المتوقع',
  'finance.expected.subtitle': 'ثلاث فئات منفصلة لا نجمعها معًا، ولا شيء منها مضمون.',
  'finance.expected.notGuaranteed': 'غير مضمون',
  'finance.expected.overdue': 'متأخر وما زال قابلًا للتحصيل',
  'finance.expected.overdueHint': 'تجاوز تاريخ استحقاقه: مبلغ مستحق، وليس نقدًا في اليد.',
  'finance.expected.scheduled': 'صادر ويحين استحقاقه',
  'finance.expected.scheduledHint': 'فواتير صادرة، مرتبة حسب تاريخ الاستحقاق. هي مستحقات مجدولة، وليست إيرادات.',
  'finance.expected.in7': 'خلال 7 أيام',
  'finance.expected.in30': 'خلال 30 يومًا',
  'finance.expected.in90': 'خلال 90 يومًا',
  'finance.expected.billing': 'فوترة محتملة',
  'finance.expected.billingHint': 'لم تصدر فواتيرها بعد: فترات عقود وتجديدات اشتراكات قد تتم فوترتها.',
  'finance.expected.contractsDue': 'فترات عقود مستحقة بلا فاتورة',
  'finance.expected.contractsUpcoming': 'فترات عقود تبدأ خلال {days} يومًا',
  'finance.expected.subscriptions': 'تجديدات اشتراكات خلال 30 يومًا ({n})',
  'finance.expected.none': 'لا شيء',
  'finance.expected.noRateLeftOut': 'الفواتير بعملة {currencies} ليس لها سعر صرف، لذلك لم ندخلها في هذه المجاميع.',
  'finance.forecast.title': 'توقع توقيت السداد',
  'finance.forecast.estimate': 'تقدير مبني على طريقة سداد الفواتير سابقًا، وهو غير مضمون.',
  'finance.forecast.expected30': 'المتوقع خلال 30 يومًا',
  'finance.forecast.median': 'التأخر الوسيط',
  'finance.forecast.p75': 'المئين الـ75',
  'finance.forecast.paidByDue': 'المدفوعة في موعدها',
  'finance.forecast.confidence': 'الثقة',
  'finance.forecast.sample': 'العينة',
  'finance.forecast.sampleValue': 'فواتير مدفوعة بالكامل: {n}',
  'finance.forecast.window': 'النافذة الزمنية',
  'finance.forecast.method': 'الطريقة',
  'finance.forecast.methodText': 'نأخذ تاريخ استحقاق كل فاتورة غير مسددة، ونحرّكه بمقدار التأخر الوسيط للفواتير المدفوعة بالكامل خلال العام الماضي.',
  'finance.forecast.daysLate': 'بعد تاريخ الاستحقاق (أيام: {n})',
  'finance.forecast.daysEarly': 'قبل تاريخ الاستحقاق (أيام: {n})',
  'finance.forecast.onTheDay': 'في يوم الاستحقاق',
  'finance.forecast.notEnough': 'لا توجد بيانات سابقة كافية للتوقع: الفواتير المدفوعة بالكامل خلال العام الماضي {sample}، والمطلوب {minimum}.',
  'finance.confidence.LOW': 'منخفضة',
  'finance.confidence.MEDIUM': 'متوسطة',
  'finance.confidence.HIGH': 'عالية',
  'finance.exp.total': 'إجمالي المصروفات',
  'finance.exp.historical': 'منها من الأشهر التاريخية: {amount}',
  'finance.exp.split': 'الرواتب والباقي',
  'finance.exp.byCategory': 'المصروفات الأخرى حسب الفئة',
  'finance.exp.categoriesHidden': 'لا نعرض الفئات ما دامت الرواتب مخفية، لأنها مع بقية الأرقام قد تكشف راتب شخص بعينه.',
  'finance.exp.noCategories': 'لا توجد مصروفات أخرى في هذه الفترة.',
  'finance.exp.uncategorised': 'دون فئة',
  'finance.exp.budgets': 'الميزانيات مقابل الفعلي',
  'finance.exp.budgetsHint': 'الميزانيات التي تتداخل مع الفترة. نعرض إنفاق كل ميزانية خلال تواريخها هي، وبعملتها.',
  'finance.exp.budget': 'الميزانية',
  'finance.exp.dates': 'التواريخ',
  'finance.exp.amount': 'المرصود',
  'finance.exp.spent': 'المنفق',
  'finance.exp.used': 'نسبة الاستهلاك',
  'finance.exp.overLimit': 'تجاوزت حدها',
  'finance.exp.overThreshold': 'تجاوزت حد التنبيه',
  'finance.exp.noBudgets': 'لا توجد ميزانية تتداخل مع هذه الفترة.',
  'finance.exp.budgetCount': 'عدد الميزانيات: {n}',
  'finance.subs.title': 'الاشتراكات',
  'finance.subs.mrr': 'الإيراد الشهري المتكرر',
  'finance.subs.arr': 'الإيراد السنوي المتكرر',
  'finance.subs.contractedHint': 'قيمة تعاقدية: ما تساويه الاتفاقيات السارية، وليست نقدًا محصّلًا.',
  'finance.subs.contracted': 'الإيراد المتكرر المتعاقد عليه، بكل عملة.',
  'finance.subs.running': 'الاشتراكات السارية',
  'finance.subs.runningHint': 'نشطة {active} · متأخرة السداد {pastDue} · تنتهي بنهاية فترتها {ending}',
  'finance.subs.cycles': 'دورات الفوترة',
  'finance.subs.cyclesValue': 'شهرية {monthly} · سنوية {yearly}',
  'finance.subs.cancelledHint': 'ملغاة {n} من أصل {total}',
  'finance.subs.byCurrency': 'حسب العملة',
  'finance.subs.subscriptions': 'الاشتراكات',
  'finance.subs.none': 'لا توجد اشتراكات سارية.',
  'finance.subs.trend': 'البدء والإلغاء شهريًا',
  'finance.subs.trendLabel': 'الاشتراكات خلال {months} شهرًا: بدأ {started} وتم إلغاء {cancelled}.',
  'finance.subs.started': 'بدأت',
  'finance.subs.cancelled': 'ملغاة',
  'finance.subs.billing': 'المفوتر والمحصّل هذا العام',
  'finance.subs.noBilling': 'لا توجد فاتورة اشتراك هذا العام.',
  'finance.subs.invoices': 'فواتير: {n}',
  'finance.subs.payments': 'دفعات: {n}',
  'finance.subs.collectedBase': 'المحصّل بالعملة الأساسية بسعر كل دفعة: {amount}',
  'finance.subs.aging': 'فواتير الاشتراكات المستحقة حسب العمر',
  'finance.subs.agingHint': 'بكل عملة على حدة، ولا نجمع عملات مختلفة.',
  'finance.subs.nothingOwed': 'لا توجد فاتورة اشتراك مستحقة.',
  'finance.subs.bucket.current': 'لم تستحق بعد',
  'finance.subs.bucket.d1_30': '1–30 يومًا',
  'finance.subs.bucket.d31_60': '31–60 يومًا',
  'finance.subs.bucket.d61_90': '61–90 يومًا',
  'finance.subs.bucket.d90_plus': 'أكثر من 90 يومًا',
  'finance.subs.upcoming': 'التجديدات خلال الـ30 يومًا القادمة',
  'finance.subs.upcomingHint': 'مواعيد فوترة قادمة، ولم تصدر فواتيرها بعد.',
  'finance.subs.noUpcoming': 'لا يوجد تجديد خلال الـ30 يومًا القادمة.',
  'finance.subs.subscription': 'الاشتراك',
  'finance.subs.nextBilling': 'الفوترة التالية',
  'finance.subs.price': 'السعر الصافي',
  'finance.contracts.title': 'العقود',
  'finance.contracts.notCollected': 'القيم التعاقدية هنا اتفاقيات، وليست أموالًا محصّلة. الإيراد الشهري المتكرر والإيراد السنوي المتكرر والقيمة الاسمية للعقد ليست إيرادات.',
  'finance.contracts.faceValue': 'القيمة الاسمية للعقد هي قيمته طوال مدته، وليست إيرادًا ولا نقدًا.',
  'finance.contracts.active': 'سارية اليوم',
  'finance.contracts.waiting': 'بانتظار البدء',
  'finance.contracts.expiring': 'تنتهي خلال {days} يومًا',
  'finance.contracts.dueTotal': 'مستحقة بلا فاتورة',
  'finance.contracts.dueHint': 'فترات عقود بدأت ولم تصدر فواتيرها: فواتير محتملة، وليست نقدًا.',
  'finance.contracts.expiringList': 'عقود تنتهي قريبًا',
  'finance.contracts.noneExpiring': 'لا يوجد عقد ينتهي خلال {days} يومًا.',
  'finance.contracts.contract': 'العقد',
  'finance.contracts.ends': 'ينتهي',
  'finance.contracts.daysLeft': 'الأيام المتبقية',
  'finance.contracts.billing': 'فترات الفوترة',
  'finance.contracts.billingHint': 'الفترات المستحقة بلا فاتورة، والتي تبدأ خلال {days} يومًا. هي فواتير محتملة، وليست نقدًا.',
  'finance.contracts.upcomingTotal': 'تبدأ خلال {days} يومًا',
  'finance.contracts.noPeriods': 'لا توجد فترة فوترة مستحقة أو قادمة.',
  'finance.contracts.period': 'الفترة',
  'finance.contracts.stateDue': 'مستحقة بلا فاتورة',
  'finance.contracts.stateUpcoming': 'قادمة',
  'finance.trust.statusLabel': 'حالة المالية',
  'finance.trust.statusTitle': 'حالة التقرير',
  'finance.trust.comparison': 'فترة المقارنة:',
  'finance.trust.generated': 'وقت الحساب',
  'finance.trust.timezone': 'المنطقة الزمنية للشركة',
  'finance.trust.legend': 'معنى كل حالة',
  'finance.trust.cutover': 'تاريخ الانتقال',
  'finance.trust.cutoverDate': 'التاريخ',
  'finance.trust.cutoverStatus': 'الحالة',
  'finance.trust.confirmedAt': 'تاريخ التأكيد',
  'finance.trust.standing': 'الفترة وتاريخ الانتقال',
  'finance.trust.cash': 'النقد حسب العملة',
  'finance.trust.basis': 'ماذا نحسب، ومتى',
  'finance.trust.fx': 'تحويل العملات',
  'finance.trust.notices': 'الملاحظات',
  'finance.trust.noNotices': 'لا توجد ملاحظات لهذه الفترة.',
  'finance.trust.monthCoverage': 'التغطية حسب الشهر',
  'finance.trust.drafts': 'المسودات',
  'finance.trust.missingDocuments': 'دون ملف',
  'finance.cutover.NONE': 'لا يوجد',
  'finance.cutover.DRAFT': 'مسودة',
  'finance.cutover.CONFIRMED': 'مؤكد',
  'finance.standing.AFTER_CUTOVER': 'بعد تاريخ الانتقال',
  'finance.standing.SPANS_CUTOVER': 'تشمل تاريخ الانتقال',
  'finance.standing.BEFORE_CUTOVER': 'قبل تاريخ الانتقال',
  'finance.standing.NO_CUTOVER': 'لا يوجد تاريخ تحول مؤكد',
  'finance.basis.revenue': 'الإيرادات',
  'finance.basis.expenses': 'المصروفات',
  'finance.basis.payroll': 'الرواتب',
  'finance.basis.pointsCash': 'النقاط المدفوعة نقدًا',
  'finance.basis.historical': 'الأشهر التاريخية',
  'finance.basis.balances': 'الأرصدة',
  'finance.basisValue.CASH_RECEIVED': 'النقد المستلم',
  'finance.basisValue.APPROVED_ON_EXPENSE_DATE': 'المعتمدة، بتاريخ المصروف',
  'finance.basisValue.PAID_PAYROLL_ONCE_ON_MONTH_END': 'الرواتب المدفوعة، مرة واحدة، في آخر يوم من الشهر',
  'finance.basisValue.PAID_BY_HAND_ON_THE_DAY': 'المدفوعة يدويًا، في يومها',
  'finance.basisValue.FINALIZED_MONTH_ON_ITS_LAST_DAY': 'الشهر المعتمد نهائيًا، في آخر أيامه',
  'finance.basisValue.OPENING_POSITION_PLUS_MOVEMENTS': 'الرصيد الافتتاحي مضافًا إليه الحركات',
  'finance.fx.payments': 'المدفوعات',
  'finance.fx.historical': 'الأشهر التاريخية',
  'finance.fx.partners': 'الشركاء',
  'finance.fx.expenses': 'المصروفات',
  'finance.fx.balances': 'الأرصدة',
  'finance.fxValue.RATE_RECORDED_WITH_THE_PAYMENT': 'السعر المسجل مع الدفعة',
  'finance.fxValue.RATE_TYPED_FOR_THE_MONTH': 'السعر الذي تم إدخاله للشهر',
  'finance.fxValue.RATE_RECORDED_WITH_THE_TRANSACTION': 'السعر المسجل مع المعاملة',
  'finance.fxValue.TODAY_RATE_FLAGGED': 'سعر اليوم، مع الإشارة إلى ذلك',
  'finance.fxValue.ORIGINAL_CURRENCY_ONLY': 'بعملتها الأصلية فقط',

  // ---- ملاحظات المالية --------------------------------------------------------------------------------
  'notice.NO_CONFIRMED_CUTOVER': 'لا يوجد تاريخ تحول مؤكد: الأرقام هي ما تم تسجيله، دون مطابقتها مع رصيد افتتاحي.',
  'notice.PERIOD_BEFORE_CUTOVER': 'تمتد الفترة إلى ما قبل تاريخ الانتقال، وما قبله معروف جزئيًا فقط.',
  'notice.HISTORICAL_RESTRICTED': 'لا يمكن قراءة الأشهر التاريخية هنا، لذلك استبعدناها.',
  'notice.MONTHS_NOT_PROVIDED': 'بعض الأشهر بلا بيانات للرواتب أو المصروفات. هي غير معروفة، وليست صفرًا.',
  'notice.OVERLAP_CHANGED': 'تغيرت السجلات الحية بعد مراجعة شهر تاريخي، فقد يتكرر حساب ذلك الشهر.',
  'notice.UNCONVERTED': 'بعض المبالغ ليس لها سعر صرف، لذلك لم ندخلها في المجاميع بالعملة الأساسية.',
  'notice.EXPENSES_AT_TODAY_RATE': 'بعض المصروفات بعملات أخرى مقوّمة بسعر اليوم.',
  'notice.REVENUE_INCLUDES_TAX': 'الإيرادات المحصلة تشمل الضريبة على بعض الفواتير.',
  'notice.PARTIAL_MONTH': 'تنتهي الفترة قبل آخر يوم في الشهر، فلا تشمل بعد المجاميع الشهرية لذلك الشهر.',
  'notice.NO_CUTOVER': 'يجب تحديد تاريخ الانتقال والمركز الافتتاحي أولًا.',
  'notice.BEFORE_CUTOVER': 'التاريخ قبل تاريخ الانتقال، فلا توجد معلومات موثوقة.',
  'notice.CUTOVER_DRAFT': 'تاريخ الانتقال ما زال مسودة: قد يتغير المركز الافتتاحي.',
  'notice.detail.months': 'الأشهر:',
  'notice.detail.cutover': 'تاريخ الانتقال:',
  'notice.detail.currencies': 'العملات:',
  'notice.detail.invoices': 'الفواتير:',

  // ---- رؤى الذكاء الاصطناعي ----------------------------------------------------------------------------
  'insights.title': 'رؤى الذكاء الاصطناعي',
  'insights.subtitle': 'ملخصات يكتبها الذكاء الاصطناعي عن التقارير المحفوظة. كل ملخص يوضح أنه من الذكاء الاصطناعي ويذكر الأرقام التي قرأها، ولا يكون مصدرًا لأي رقم.',
  'insights.availability': 'التوفر',
  'insights.state.ENABLED': 'مفعّل',
  'insights.state.DISABLED': 'متوقف',
  'insights.state.NOT_CONFIGURED': 'غير معدّ',
  'insights.state.BUDGET_EXCEEDED': 'نفدت الميزانية',
  'insights.stateHint.ENABLED': 'يكتب الذكاء الاصطناعي ملخصات للتقارير الجديدة، ضمن ميزانية هذا الشهر.',
  'insights.stateHint.DISABLED': 'شروح الذكاء الاصطناعي متوقفة (على الخادم أو في إعدادات لوحة الإدارة)، فلا نرسل أي شيء.',
  'insights.stateHint.NOT_CONFIGURED': 'الذكاء الاصطناعي مفعّل لكنه غير معدّ على الخادم، فلا يوجد نموذج نسأله.',
  'insights.stateHint.BUDGET_EXCEEDED': 'نفدت ميزانية الرموز لهذا الشهر، وستنتظر الملخصات حتى الشهر القادم.',
  'insights.usage': 'استهلاك هذا الشهر',
  'insights.tokens': '{used} من {budget} رمز',
  'insights.cost': 'التكلفة التقديرية',
  'insights.costEstimate': 'تقدير حسب الأسعار في الإعدادات، وليس فاتورة.',
  'insights.costEstimateAsOf': 'تقدير حسب أسعار الإعدادات بتاريخ {date}، وليس فاتورة.',
  'insights.noCost': 'لا يوجد تقدير، لأن الأسعار غير محددة.',
  'insights.model': 'النموذج',
  'insights.list': 'الملخصات',
  'insights.none': 'لا يوجد ملخص بعد',
  'insights.noneHint': 'بعد تفعيل الذكاء الاصطناعي، يكتب ملخصات للتقارير اليومية والأسبوعية والشهرية.',
  'insights.noneHintEnabled': 'سيكون للتقرير المجدول القادم ملخص.',
  'insights.briefing': 'ملخص',
  'insights.kindTitle.DAILY': 'الملخص اليومي',
  'insights.kindTitle.WEEKLY': 'الملخص الأسبوعي',
  'insights.kindTitle.MONTHLY': 'التقرير الشهري',
  'insights.kindTitle.CUSTOM': 'تقرير الفترة المخصصة',
  'insights.revision': 'نسخة الشرح {n}',
  'insights.openReport': 'فتح التقرير',
  'insights.regenerate': 'إعادة التوليد',
  'insights.regenerated': 'تم طلب شرح جديد، وسيظهر هنا بعد كتابته.',
  'aiAct.ask': 'اطلب شرحًا من الذكاء الاصطناعي',
  'aiAct.tryAgain': 'حاول مرة ثانية',
  'aiAct.regenerate': 'إعادة التوليد',
  'aiAct.regenerateTitle': 'هل تطلب شرحًا جديدًا من الذكاء الاصطناعي؟',
  'aiAct.regenerateBody': 'يكتب الذكاء الاصطناعي شرحًا جديدًا من الأرقام المحفوظة نفسها، وهذا يستهلك رموزًا. يبقى الشرح المحفوظ في السجل، ولا تطلب اللوحة شرحًا جديدًا من تلقاء نفسها.',
  'aiAct.regenerateConfirm': 'إعادة التوليد',
  'aiAct.requested': 'طلبنا الشرح من الذكاء الاصطناعي. سيظهر هنا بعد كتابته، ونحفظه من وقتها.',
  'aiAct.reused': 'هذا هو الشرح المحفوظ، ولم نطلب من الذكاء الاصطناعي شرحًا جديدًا.',
  'aiAct.inProgress': 'جاري كتابة الشرح، وسيظهر هنا عندما يكون جاهزًا.',
  'aiAct.unavailable': 'شروح الذكاء الاصطناعي متوقفة أو غير معدّة.',
  'aiAct.writtenFor': 'هذا الشرح مكتوب للنسخة {revision}، ومحفوظ كما هو. اضغط «إعادة التوليد» لتطلب شرحًا لهذه النسخة.',
  'aiAct.writtenForOnly': 'هذا الشرح مكتوب للنسخة {revision} من هذا التقرير، ومحفوظ كما هو.',
  'aiAct.savedFirst': 'المحفوظ أولًا: نطلب الشرح من الذكاء الاصطناعي مرة واحدة ونحفظه. فتحه أو طلبه مرة ثانية لا يستدعي الذكاء الاصطناعي، ولا يستدعيه إلا زر «إعادة التوليد».',
  'reports.recalculate': 'إعادة الحساب',
  'reports.recalculateTitle': 'هل تريد إعادة قراءة هذه الفترة؟',
  'reports.recalculateBody': 'نقرأ الفترة من سجلاتها مرة ثانية. إذا تغيرت السجلات نحفظ نسخة جديدة ونحتفظ بهذه، وإلا فلا يتغير شيء. ولا نعيد كتابة شرح الذكاء الاصطناعي المحفوظ.',
  'reports.recalculateConfirm': 'إعادة الحساب',
  'reports.recalculated': 'تمت إعادة الحساب وحفظ النسخة {revision}، مع الاحتفاظ بالنسخة السابقة.',
  'reports.recalculateUnchanged': 'لم يتغير شيء، ويبقى التقرير المحفوظ كما هو.',
  'reports.snapshot.reused': 'هذه الفترة محفوظة من قبل، لذلك نعرض تقريرها المحفوظ دون إعادة أي حساب.',
  'insights.itemTokens': 'الرموز: إدخال {input}، إخراج {output}',
  'insights.itemCost': 'التكلفة التقديرية: {cost}',
  'insights.failure': 'السبب:',

  // ---- التقارير التنفيذية ------------------------------------------------------------------------------
  'reports.title': 'التقارير التنفيذية',
  'reports.subtitle': 'لقطات محفوظة للأيام والأسابيع والأشهر والفترات. لا نكتب فوقها أبدًا، وأي تغيير في مصادرها ينشئ نسخة جديدة.',
  'reports.kind.DAILY': 'يومي',
  'reports.kind.WEEKLY': 'أسبوعي',
  'reports.kind.MONTHLY': 'شهري',
  'reports.kind.CUSTOM': 'فترة مخصصة',
  'reports.filter.kind': 'النوع',
  'reports.filter.since': 'تبدأ من',
  'reports.filter.until': 'تنتهي حتى',
  'reports.col.period': 'الفترة',
  'reports.col.kind': 'النوع',
  'reports.col.revision': 'النسخة',
  'reports.col.generatedBy': 'أنشأه',
  'reports.col.computed': 'وقت الحساب',
  'reports.col.finalized': 'الاعتماد النهائي',
  'reports.col.ai': 'الذكاء الاصطناعي',
  'reports.col.coverage': 'التغطية',
  'reports.generatedBy.SYSTEM': 'النظام (مجدول)',
  'reports.generatedBy.MANUAL': 'يدوي',
  'reports.notFinalized': 'ليس بعد',
  'reports.revisionShort': 'النسخة {n}',
  'reports.none': 'لا توجد تقارير بعد',
  'reports.noneHint': 'ننشئ التقارير حسب جدول، كل يوم وأسبوع وشهر، ويمكنك إنشاؤها من هنا عبر «إنشاء لقطة».',
  'reports.noneFiltered': 'لا يوجد تقرير مطابق',
  'reports.coverage.people': 'لهم درجة {scored} · دون أدلة كافية {none}',
  'reports.coverage.health': 'وزن الصحة {weight}%',
  'reports.coverage.truncated': 'أدلة تجاوزت حد القراءة',
  'reports.coverage.notices': 'ملاحظات: {n}',
  'reports.aiStatus.READY': 'جاهز',
  'reports.aiStatus.PENDING': 'قيد الانتظار',
  'reports.aiStatus.RUNNING': 'قيد الكتابة',
  'reports.aiStatus.FAILED': 'فشل',
  'reports.aiStatus.DISABLED': 'متوقف',
  'reports.aiStatus.NOT_CONFIGURED': 'غير معدّ',
  'reports.aiStatus.BUDGET_EXCEEDED': 'نفدت الميزانية',
  'reports.aiStatus.NOT_REQUESTED': 'لا يوجد بعد',
  'reports.aiStatus.ENABLED': 'مفعّل',
  'reports.snapshot.title': 'إنشاء لقطة',
  'reports.snapshot.hint': 'تحفظ الفترة كما نحسبها الآن. المصادر نفسها تعطي التقرير نفسه، وإذا تغيرت المصادر ننشئ نسخة جديدة.',
  'reports.snapshot.kind': 'النوع',
  'reports.snapshot.day': 'اليوم',
  'reports.snapshot.dayInWeek': 'أي يوم من الأسبوع',
  'reports.snapshot.month': 'الشهر',
  'reports.snapshot.submit': 'إنشاء اللقطة',
  'reports.snapshot.saving': 'جاري الحساب…',
  'reports.snapshot.saved': 'تم حفظ لقطة التقرير: النسخة {revision}، {status}.',
  'reports.snapshot.open': 'فتح اللقطة (النسخة {revision})',
  'reports.snapshot.note': 'نكتب سجل اللوحة فقط، ولا يتغير أي سجل من سجلات الشركة.',
  'reports.back': 'كل التقارير',
  'reports.exportFailed': 'فشل التصدير: {message}',
  'reports.detail.title': 'تقرير {kind}',
  'reports.detail.superseded': 'مستبدل',
  'reports.detail.notCurrent': 'توجد نسخة أحدث تحل محل هذه النسخة.',
  'reports.detail.openCurrent': 'فتح النسخة {n}',
  'reports.detail.period': 'الفترة',
  'reports.detail.timezone': 'المنطقة الزمنية',
  'reports.detail.generatedBy': 'أنشأه',
  'reports.detail.requestedBy': 'بطلب من {name}',
  'reports.detail.versions': 'النسخ',
  'reports.detail.settingsVersion': 'نسخة الإعدادات {v}',
  'reports.detail.liveWhenComputed': 'كانت الفترة جارية عند حساب هذا التقرير، لذلك تتوقف أرقامه عند {time}.',
  'reports.detail.storedNote': 'كل ما يلي هو التقرير كما تم حفظه، دون إعادة حساب. نقرأ الأسماء الآن، والحساب المحذوف يظهر باسم «موظف سابق».',
  'reports.detail.revisions': 'سجل التغييرات',
  'reports.detail.current': 'الحالية',
  'reports.detail.viewing': 'المعروضة',
  'reports.detail.reason.SOURCES_CHANGED': 'تغيرت مصادره',
  'reports.detail.unreadable': 'الأرقام المحفوظة في هذا التقرير بصيغة لا تستطيع هذه الصفحة قراءتها، لكنها موجودة في ملفات التصدير.',
  'reports.detail.figures': 'الأرقام المحفوظة',
  'reports.detail.figuresHint': 'كما تم حسابها في {time}. الروابط تفتح الفترة نفسها بأرقامها الحالية.',
  'reports.detail.ai': 'شرح الذكاء الاصطناعي لهذا التقرير',
  'reports.detail.months': 'المالية شهريًا',
  'reports.detail.people': 'أداء العمل',
  'reports.detail.peopleHint': 'الأفراد {people} · لهم درجة {scored} · دون أدلة كافية {none}',
  'reports.detail.noPeople': 'لا يوجد أحد في هذا التقرير.',
  'reports.detail.score': 'الدرجة',
  'reports.detail.confidence': 'الثقة',
  'reports.detail.evidence': 'الأدلة',
  'reports.detail.measurableWeight': 'الوزن القابل للقياس',
  'reports.detail.peopleNote': 'درجة أداء العمل مبنية على أدلة العمل المنسوبة لكل شخص. ليست نقاطًا ولا راتبًا ولا قرارًا من الموارد البشرية، ولا يدخل فيها الحضور ولا الورديات ولا الإجازات.',
  'reports.detail.urgentAsComputed': 'ما كان يحتاج إلى اهتمام عند حساب التقرير ({time}).',
  'reports.confidence.HIGH': 'عالية',
  'reports.confidence.MEDIUM': 'متوسطة',
  'reports.confidence.LOW': 'منخفضة',
  'reports.confidence.NONE': 'لا توجد',
};
